import React, { Component, Fragment, createRef } from 'react'
import PropTypes from 'prop-types'
import { Route, Link } from 'react-router-dom'
import track from 'react-tracking'
import Notification, { Tooltip } from '@ynap/notification'
import { Button } from '@ynap/button'
import BEMHelper from '@ynap/bem-helper'
import AddressForm, { mapAddressValues } from '@ynap/address-form-v1'
import Address from '@ynap/address'
import Overlay from '@ynap/overlay'
import Skeleton from '@ynap/skeleton'
import EmptyList from '@ynap/empty-list'
import { myaccountPageData } from '../analytics'
import { trackAddressBookAction } from './analytics'
import { scrollIntoView } from '@ynap/scroll-utils'
import { getStateNameByCode, getCountryNameByCode } from '@ynap/locale-utils'

const bem = new BEMHelper('AddressBook')

@track({})
class AddressBook extends Component {
    static propTypes = {
        messages: PropTypes.shape({
            titles: PropTypes.objectOf(PropTypes.func).isRequired,
            countries: PropTypes.objectOf(PropTypes.func).isRequired,
            addressBook: PropTypes.shape({
                edit: PropTypes.func.isRequired,
                delete: PropTypes.func.isRequired,
                undo: PropTypes.func.isRequired,
                addTitle: PropTypes.func.isRequired,
                editTitle: PropTypes.func.isRequired,
                title: PropTypes.func.isRequired,
                saved: PropTypes.func.isRequired,
                deleted: PropTypes.func.isRequired,
                addAddressLink: PropTypes.func.isRequired,
                description: PropTypes.func.isRequired,
                deleteError: PropTypes.func.isRequired,
                emptyAddressesTitle: PropTypes.func.isRequired,
                emptyAddressesDetails: PropTypes.func.isRequired,
                emptyAddressLinkTitle: PropTypes.func.isRequired,
                pageError: PropTypes.func.isRequired,
                retryNow: PropTypes.func.isRequired,
            }).isRequired,
        }).isRequired,
        addressBook: PropTypes.object,
        className: PropTypes.string,
        fetchAddresses: PropTypes.func,
        fetchCountries: PropTypes.func,
        getArrowDimensions: PropTypes.func,
        updateAddress: PropTypes.func,
        config: PropTypes.any,
        deleteAddress: PropTypes.func,
        undoDeleteAddress: PropTypes.func,
        addAddress: PropTypes.func,
        locale: PropTypes.shape({
            id: PropTypes.string,
            country: PropTypes.string,
            countries: PropTypes.arrayOf(PropTypes.string),
            states: PropTypes.objectOf(PropTypes.object),
        }).isRequired,
        titles: PropTypes.arrayOf(PropTypes.string),
        location: PropTypes.object,
        push: PropTypes.func,
        skeletonAddresses: PropTypes.number,
        countries: PropTypes.object,
    }

    static defaultProps = {
        addressBook: {},
        location: {},
        skeletonAddresses: 3,
        getArrowDimensions,
    }

    constructor(props) {
        super(props)
        this.trackAction = this.trackAction.bind(this)
        this.state = {
            notification: undefined,
            isProvincesFetching: false,
            isProvincesFetched: false,
        }
        this.addressBookTop = createRef()
    }

    @track(
        myaccountPageData({
            name: 'address book',
            type: 'form entry page',
            subCategory1: 'address book',
        })
    )
    componentDidMount() {
        this.props.fetchAddresses()

        if (!this.props.countries && this.props.fetchCountries) {
            this.props.fetchCountries()
        }
    }

    componentDidUpdate(prevProps) {
        const { addressBook, push, locale, location, fetchProvinces, fetchAllProvinces, countries } = this.props
        const { addresses, fetching } = addressBook

        const prevFetching = prevProps?.addressBook?.fetching
        const prevAddresses = prevProps?.addressBook?.addresses

        if (this.state.notification && !location.pathname.match(/\/addresses$/i)) {
            this.setState({
                notification: undefined,
            })
        }

        if (addresses && countries && !fetching && !this.state.isProvincesFetched && !this.state.isProvincesFetching) {
            const uniqueCountries = addresses.reduce((accumulator, currentValue) => {
                if (!accumulator.includes(currentValue.country)) {
                    accumulator.push(currentValue.country)
                }
                return accumulator
            }, [])

            const singleCountryMissingProvinces =
                uniqueCountries.length === 1 && !countries[`${uniqueCountries[0]}`]?.provinces
            const multipleCountriesMissingProvinces =
                uniqueCountries.length > 1 &&
                uniqueCountries.find((addressCountry) => !countries[`${addressCountry}`]?.provinces)
            const getProvinces = singleCountryMissingProvinces
                ? () => fetchProvinces(uniqueCountries[0])
                : multipleCountriesMissingProvinces
                ? () => fetchAllProvinces()
                : null

            if (getProvinces) {
                this.setState((state) => ({ ...state, isProvincesFetching: true }))
                getProvinces().then(() => {
                    this.setState((state) => ({
                        ...state,
                        isProvincesFetching: false,
                        isProvincesFetched: true,
                    }))
                })
            }
        }

        const match = location.pathname.match(/\/addresses\/(add|edit)/)
        if (match && addresses && prevAddresses && !fetching && prevFetching) {
            // create object keyed on address id for all addresses in the old address list.
            const prevAddressesRecord = prevAddresses.reduce((accum, address) => {
                accum[address.addressId] = true
                return accum
            }, {})

            // check each address in new list to see if it doesn't appear as a key in prevAddressesRecord
            // if it doesn't, then that is our edited/new address.
            const newOrEditedAddress = addresses.find((address) => !prevAddressesRecord[address.addressId])

            if (newOrEditedAddress) {
                const type = match[1] === 'edit' ? 'update' : 'add'
                push(`/${locale.id}/account/addresses`)
                this.setState({
                    notification: {
                        type,
                        addressLine: newOrEditedAddress.addressLine[0],
                    },
                })
            }
        }
    }

    @track((props, state, [event]) => trackAddressBookAction(event.currentTarget.getAttribute('data-action')))
    trackAction() {}

    render() {
        const {
            addressBook,
            deleteAddress,
            undoDeleteAddress,
            addAddress,
            updateAddress,
            messages,
            fetchAddresses,
            locale,
            config,
            className,
            push,
            skeletonAddresses,
            countries,
            getArrowDimensions,
            nativeApp,
        } = this.props

        const { addresses, error: fetchAddressesError } = addressBook
        const notificationState = this.state.notification
        const provincesState = this.state.isProvincesFetched
        const { fullPageHeader } = config?.head?.flags || {}

        return (
            <div className={bem(null, null, className)}>
                {fetchAddressesError ? (
                    <EmptyList
                        className={bem('empty')}
                        icon={<div className={bem('errorIcon')} />}
                        variant={nativeApp ? '' : 'myAccountError'}
                        title={messages.addressBook.pageError()}
                    >
                        <Button onClick={() => fetchAddresses()} type="primary-link" className={bem('emptyLink')}>
                            {messages.addressBook.retryNow()}
                        </Button>
                    </EmptyList>
                ) : (
                    <Fragment>
                        {addresses && addresses.length === 0 ? (
                            <EmptyList
                                className={bem('empty')}
                                variant={nativeApp ? '' : 'myAccountError'}
                                icon={<div className={bem('emptyStateIcon')} />}
                                title={messages.addressBook.emptyAddressesTitle()}
                                details={messages.addressBook.emptyAddressesDetails()}
                            >
                                <Button
                                    component={Link}
                                    to={`/${locale.id}/account/addresses/add`}
                                    type="primary-link"
                                    className={bem('emptyLink')}
                                    data-action="add"
                                    onClick={this.trackAction}
                                >
                                    {messages.addressBook.emptyAddressLinkTitle()}
                                </Button>
                            </EmptyList>
                        ) : (
                            <Fragment>
                                {!fullPageHeader && <div className={bem('title')}>{messages.addressBook.title()}</div>}

                                {notificationState && (
                                    <Notification
                                        className={bem('saved')}
                                        type="success"
                                        message={messages.addressBook.saved({
                                            type: notificationState.type,
                                            addressLine: notificationState.addressLine,
                                        })}
                                    />
                                )}

                                {messages.addressBook.description && (
                                    <div className={bem('description')}>{messages.addressBook.description()}</div>
                                )}

                                <section className={bem('list')}>
                                    <article className={bem('tile')}>
                                        <Button
                                            component={Link}
                                            className={bem('add')}
                                            to={`/${locale.id}/account/addresses/add`}
                                            data-action="add"
                                            onClick={this.trackAction}
                                        >
                                            <div className={bem('addAddress')}>
                                                <div className={bem('addressImage')} />
                                                <div className={bem('addMessage')}>
                                                    {messages.addressBook.addAddressLink()}
                                                </div>
                                            </div>
                                        </Button>
                                    </article>

                                    {addresses && countries && provincesState
                                        ? addresses.map((address) => {
                                              return (
                                                  <article key={address.addressId} className={bem('tile')}>
                                                      {address.isDeleted ? (
                                                          <div className={bem('address')}>
                                                              <div className={bem('undoMessage')}>
                                                                  {messages.addressBook.deleted()}
                                                                  <div>
                                                                      {address.addressLine[1]
                                                                          ? `${address.addressLine[0]}, ${address.addressLine[1]}`
                                                                          : address.addressLine[0]}
                                                                  </div>
                                                              </div>
                                                              <div className={bem('actions')}>
                                                                  <Button
                                                                      component={Link}
                                                                      to={`/${locale.id}/account/addresses/`}
                                                                      className={bem('undo')}
                                                                      data-action="undo"
                                                                      onClick={(event) => {
                                                                          this.trackAction(event)
                                                                          /* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
                                                                          const {
                                                                              isDeleted,
                                                                              loading,
                                                                              addressId: removeAddressId,
                                                                              ...values // we have removed the keys from address that we don't want to send to undoDeleteAddress
                                                                          } = address
                                                                          /* eslint-enable no-unused-vars, @typescript-eslint/no-unused-vars */
                                                                          undoDeleteAddress({
                                                                              addressId: address.addressId,
                                                                              address: values,
                                                                          })
                                                                      }}
                                                                      disabled={address.loading}
                                                                  >
                                                                      {messages.addressBook.undo()}
                                                                  </Button>
                                                              </div>
                                                          </div>
                                                      ) : (
                                                          <Address
                                                              messages={messages}
                                                              className={bem('address')}
                                                              {...address}
                                                              countryCode={address.country}
                                                              countryName={getCountryNameByCode(address.country, {
                                                                  countries,
                                                              })}
                                                              country={getCountryNameByCode(address.country, {
                                                                  countries,
                                                              })}
                                                              stateName={
                                                                  address.state
                                                                      ? getStateNameByCode(
                                                                            address.state,
                                                                            address.country,
                                                                            { countries }
                                                                        )
                                                                      : undefined
                                                              }
                                                          >
                                                              <div className={bem('actions')}>
                                                                  <div className={bem('action')}>
                                                                      <Button
                                                                          component={Link}
                                                                          className={bem('edit')}
                                                                          to={`/${locale.id}/account/addresses/edit/${address.addressId}`}
                                                                          data-action="edit"
                                                                          onClick={this.trackAction}
                                                                      >
                                                                          {messages.addressBook.edit()}
                                                                      </Button>
                                                                  </div>
                                                                  <div className={bem('action')}>
                                                                      <Tooltip
                                                                          type="error"
                                                                          message={messages.addressBook.deleteError()}
                                                                          defaultOpen={!!address.loadError}
                                                                          getArrowDimensions={getArrowDimensions}
                                                                      >
                                                                          <Button
                                                                              component={Link}
                                                                              to={`/${locale.id}/account/addresses/`}
                                                                              className={bem('delete')}
                                                                              data-action="delete"
                                                                              onClick={(event) => {
                                                                                  this.trackAction(event)
                                                                                  deleteAddress({
                                                                                      addressId: address.addressId,
                                                                                  })
                                                                              }}
                                                                              disabled={!!address.loading}
                                                                          >
                                                                              {messages.addressBook.delete()}
                                                                          </Button>
                                                                      </Tooltip>
                                                                  </div>
                                                              </div>
                                                          </Address>
                                                      )}
                                                  </article>
                                              )
                                          })
                                        : Array(skeletonAddresses)
                                              .fill({})
                                              .map((ignore, i) => (
                                                  <article key={i} className={bem('tile')}>
                                                      <Skeleton
                                                          count={5}
                                                          align="left"
                                                          containerClassName={bem('addressSkeleton')}
                                                      />
                                                  </article>
                                              ))}
                                </section>
                            </Fragment>
                        )}
                    </Fragment>
                )}

                <Route
                    path="/:locale/account/addresses/add"
                    render={() => (
                        <Overlay
                            className="addressForm"
                            title={messages.addressBook.addTitle()}
                            onClose={() => push(`/${locale.id}/account/addresses`)}
                        >
                            <AddressForm
                                key="form"
                                className={bem('form')}
                                config={config}
                                locale={locale}
                                country={locale.country}
                                initialValues={{ addressType: 'ShippingAndBilling', country: locale.country }}
                                messages={messages}
                                onSubmit={(values) => {
                                    scrollIntoView(this.addressBookTop)

                                    const mappedValues = mapAddressValues(values, locale)

                                    if (
                                        mappedValues.country === 'JP' &&
                                        mappedValues.pronunciations &&
                                        (mappedValues.pronunciations[0].firstName ||
                                            mappedValues.pronunciations[0].lastName)
                                    ) {
                                        mappedValues.pronunciations[0].locale = 'ja_JP'
                                    }

                                    return addAddress({
                                        address: { ...mappedValues, externalAddressValidation: 'true' },
                                    })
                                }}
                                isAddressBook
                            />
                        </Overlay>
                    )}
                />

                <Route
                    path="/:locale/account/addresses/edit/:id"
                    render={(matchProps) => {
                        if (!addresses) {
                            return null
                        }

                        const address = addresses.find((address) => address.addressId === matchProps.match.params.id)

                        if (!address) {
                            return null
                        }

                        return (
                            <Overlay
                                className="addressForm"
                                title={messages.addressBook.editTitle()}
                                onClose={() => push(`/${locale.id}/account/addresses`)}
                            >
                                <AddressForm
                                    key="form"
                                    className={bem('form')}
                                    config={config}
                                    locale={locale}
                                    country={locale.country}
                                    initialValues={address}
                                    messages={messages}
                                    onSubmit={(values) => {
                                        scrollIntoView(this.addressBookTop)
                                        return updateAddress({
                                            address: {
                                                addressId: address.addressId,
                                                ...values,
                                                externalAddressValidation: 'true',
                                            },
                                        })
                                    }}
                                    isAddressBook
                                />
                            </Overlay>
                        )
                    }}
                />
            </div>
        )
    }
}

export default AddressBook

function getArrowDimensions(target, notification, tooltipProps) {
    const { arrowMargin } = tooltipProps

    const parentRect = target.parentElement.parentElement.parentElement.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()
    const tooltipRect = notification.getBoundingClientRect()

    const left = Math.min(
        tooltipRect.width - arrowMargin,
        Math.max(arrowMargin, targetRect.x - parentRect.x + arrowMargin)
    )
    const width = parentRect.width

    return { left, width }
}
